import React, { useEffect, useRef, useState } from 'react';
import { useLocale } from '../../contexts/LocaleContext.jsx';
import { LOCALE_PREFERENCE_STATUS } from '../../_helpers/localePreference.js';
import { richMessage } from '../../_helpers/richMessage.jsx';
import '../../styles/partial/LanguageSelector.css';

const join = (...classes) => classes.filter(Boolean).join(' ');

const { READY, ERROR } = LOCALE_PREFERENCE_STATUS;

/**
 * Language selector (ENG-387; ENG-380 places it in the profile's Preferences
 * section, where it is the pilot's one way to pick a language).
 *
 * A native `<select>` on purpose: it is keyboard-operable and announced with
 * its label and option count by every screen reader without ARIA plumbing.
 * Each option is displayed in its own language (autonym), so the option
 * carries `lang`/`dir` for correct pronunciation and glyph shaping. Renders
 * nothing when there is only one locale to choose from.
 *
 * A change applies at once (there is no Save), so the outcome is confirmed
 * in a polite live region — in the language just chosen, which is also the
 * one `<html lang>` now declares, so a screen reader switches voice on the
 * confirmation rather than mid-page. A tag the runtime refuses (not enabled,
 * or gone from the registry) is reported the same way instead of the select
 * silently snapping back.
 *
 * For a signed-in listener the choice is also saved to the account
 * (ENG-379): a second hint paragraph says so once the account has answered,
 * and a save that fails is announced in the same region — the page keeps the
 * language the listener can see took effect, and a Try Again offers the same
 * save. The button stays where it is while the retry is in flight (marked
 * `aria-disabled`, so it keeps keyboard focus) and the region reads a neutral
 * "saving" line rather than a confirmation the network has not given yet;
 * once the retry lands and the button goes, focus is handed to the select so
 * a keyboard user is not dropped at the top of the page.
 */
function LanguageSelector({ id = 'language-selector', classNames = {} }) {
  const { locale, locales, setLocale, t, preference } = useLocale();
  // The last outcome: the locale entry just chosen, or `null` for a refused
  // tag; `undefined` until the user acts. The region is rendered from the
  // start so assistive technology is already watching it when text appears.
  const [outcome, setOutcome] = useState(undefined);
  const selectRef = useRef(null);
  // Whether the Try Again held focus when it was last pressed, so the focus
  // can be handed on if the button leaves while it still has it.
  const retryHadFocusRef = useRef(false);

  // Only the latest save can be the failed one, and it is always for `outcome`.
  const saveFailed = Boolean(outcome && preference.saveError);
  const retrying = saveFailed && preference.saving;

  useEffect(() => {
    if (saveFailed || !retryHadFocusRef.current) return;
    retryHadFocusRef.current = false;
    // The button unmounted while focused, which browsers answer by dropping focus to <body>.
    if (document.activeElement === document.body) selectRef.current?.focus();
  }, [saveFailed]);

  if (locales.length < 2) return null;

  const hintId = `${id}-hint`;
  const accountHintId = `${id}-hint-account`;
  // The account wording appears once the load has settled without the account
  // refusing (`ready`, or `error` — a choice is still sent then); during the
  // load the device wording stands, so the hint does not flip twice on a
  // backend that turns out not to have shipped the preference.
  const accountBacked = [READY, ERROR].includes(preference.status);

  const handleChange = (event) => {
    const tag = event.target.value;
    if (!setLocale(tag)) {
      setOutcome(null);
      return;
    }
    // Every option is an entry of `locales`; the tag stands in should one not be.
    setOutcome(locales.find((entry) => entry.tag === tag) ?? { tag, nativeName: tag });
  };

  const handleRetry = (event) => {
    if (retrying) return;
    retryHadFocusRef.current = event.currentTarget === document.activeElement;
    preference.saveError.retry();
  };

  // The autonym is a run of another script and direction inside the new
  // locale's sentence, so it is bidi-isolated (and pronounced in its own
  // language) rather than interpolated as plain text, which under an RTL
  // locale could carry the full stop off to the wrong end.
  const withAutonym = (message) =>
    richMessage(message, { language: <bdi lang={outcome.tag} dir={outcome.dir}>{outcome.nativeName}</bdi> });

  const statusText = outcome === undefined
    ? null
    : outcome === null
      ? t('language.unavailable')
      : retrying
        ? withAutonym(t('language.saving'))
        : saveFailed
          ? withAutonym(t('language.saveFailed'))
          : withAutonym(t('language.changed'));

  return (
    <div className={join('language-selector', classNames.root)}>
      <label htmlFor={id} className={join('language-selector-label', classNames.label)}>
        {t('language.label')}
      </label>
      <select
        id={id}
        ref={selectRef}
        className={join('language-selector-select', classNames.select)}
        value={locale}
        onChange={handleChange}
        aria-describedby={accountBacked ? `${hintId} ${accountHintId}` : hintId}
      >
        {locales.map((entry) => (
          <option key={entry.tag} value={entry.tag} lang={entry.tag} dir={entry.dir}>
            {entry.nativeName}
          </option>
        ))}
      </select>
      <p id={hintId} className={join('language-selector-hint', classNames.hint)}>
        {t('language.hint')}
      </p>
      {accountBacked && (
        <p id={accountHintId} className={join('language-selector-hint', classNames.hint)}>
          {t('language.hintAccount')}
        </p>
      )}
      <p role="status" className={join('language-selector-status', classNames.status)}>
        {statusText}
      </p>
      {saveFailed && (
        <button
          type="button"
          className={join('language-selector-retry', classNames.retry)}
          onClick={handleRetry}
          aria-disabled={retrying || undefined}
        >
          {t('common.actions.tryAgain')}
        </button>
      )}
    </div>
  );
}

export default LanguageSelector;
